'use client';

import { useState, useEffect } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselNavigation,
  CarouselControls,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import { ComponentSection } from '@/app/_components/core/component-section';
import { getItemTrackId, getSectionProps, getTrackingPrefix } from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { useResponsiveSlidesPerView } from '@/app/_hooks/use-responsive-slides-per-view';
import { cn } from '@/app/_lib/utils';
import { isPortraitIndex, getItemBasisClass } from './carousel.utils';
import { CarouselImageItem } from './components/carousel-image-item';
import { CarouselIntro } from './components/carousel-intro';
import type { CarouselComponentProps } from './carousel.types';

// Design token classes (from carousel.token.css)
const designTokenCarouselContentClasses =
  'ml-carousel-item-gap-ml-mobile lg:ml-carousel-item-gap-ml-desktop';
const designTokenCarouselItemClasses =
  'pl-carousel-item-gap-pl-mobile lg:pl-carousel-item-gap-pl-desktop';

export default function CarouselComponent({
  component,
  componentPosition,
  isNested,
}: CarouselComponentProps) {
  const {
    carouselImages,
    intro,
    orientation = 'horizontal',
    showNavigation = true,
    showDots = true,
    showCaption = true,
    autoplay = true,
    autoplayInterval = 3000,
    loop = true,
    fade = false,
    slidesPerView = 4,
    displayType = 'lp',
    navigationVariant = 'outline',
    className,
    onSlideChange,
  } = component;

  // layoutSplit only applies for L/P pattern, not for square images
  // TODO: layoutSplit may become configurable from T3 in the future
  const isSquare = displayType === 'square';
  const layoutSplit = !isSquare;

  const [api, setApi] = useState<CarouselApi>();

  const currentSlidesPerView = useResponsiveSlidesPerView(slidesPerView);
  const canScroll = carouselImages.length > currentSlidesPerView;
  const showPeek = carouselImages.length > 1;
  const carouselLabel = useLabel('carousel.imageGallery', 'Image gallery');

  // Autoplay and fade are now handled by core/carousel via props
  const enableAutoplay = autoplay && canScroll;
  const enableFade = fade && slidesPerView === 1;

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      onSlideChange?.(api);
    };

    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api, onSlideChange]);

  if (carouselImages.length === 0) {
    return null;
  }

  const carouselOpts = {
    loop: canScroll ? loop : false,
    align: 'start' as const,
    slidesToScroll: 1,
    watchDrag: canScroll,
  };

  const carouselKey = `carousel-${canScroll}-${currentSlidesPerView}`;
  const trackingPrefix = getTrackingPrefix(component);

  return (
    <ComponentSection isNested={isNested}
      {...getSectionProps(component, componentPosition, isNested)}
      className={cn(
        'container py-section-padding-y-mobile xl:py-section-padding-y-desktop',
        showPeek && 'pr-0 sm:pr-4 xl:pr-6'
      )}
    >
      {intro && (
        <div className={showPeek ? 'pr-4 sm:pr-0' : undefined}>
          <CarouselIntro intro={intro} trackingPrefix={trackingPrefix} />
        </div>
      )}

      <Carousel
        key={carouselKey}
        orientation={orientation}
        opts={carouselOpts}
        autoplay={enableAutoplay}
        autoplayDelay={autoplayInterval}
        fade={enableFade}
        setApi={setApi}
        ariaLabel={carouselLabel}
        className={className}
      >
        <CarouselContent className={designTokenCarouselContentClasses}>
          {carouselImages.map((image, index) => {
            const isPortrait = layoutSplit && isPortraitIndex(index);
            const itemBasisClass = getItemBasisClass(
              slidesPerView,
              isSquare,
              isPortrait
            );

            return (
              <CarouselItem
                key={image.id}
                className={cn(itemBasisClass, designTokenCarouselItemClasses)}
              >
                <CarouselImageItem
                  image={image}
                  index={index}
                  layoutSplit={layoutSplit}
                  showCaption={showCaption}
                  slidesPerView={slidesPerView}
                  displayType={displayType}
                  priority={componentPosition === 0 && index === 0}
                  trackingPrefix={
                    trackingPrefix
                      ? `${trackingPrefix}-image-link-${getItemTrackId(image.id, index)}`
                      : undefined
                  }
                />
              </CarouselItem>
            );
          })}
        </CarouselContent>

        {(showNavigation || showDots) && canScroll && (
          <CarouselControls className="mt-10 lg:mt-12">
            {showNavigation && (
              <CarouselNavigation variant={navigationVariant} api={api} />
            )}
            {showDots && <CarouselDots api={api} />}
          </CarouselControls>
        )}
      </Carousel>
    </ComponentSection>
  );
}

export { CarouselComponent };
